import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import AiFeedbackButtons from "./AiFeedbackButtons";
import MessageFeedback from "./MessageFeedback";
import {
  clearPendingTextDraftFeedback,
  getPendingTextDraftFeedback,
  getTextAiDraftContent,
  getTextAiResponseHistoryId,
  setPendingTextDraftFeedback,
} from "../../../redux/slices/aiSlice";
import { resolveLeadOpportunityId } from "../../../helpers/opportunityHelper";
import { MESSAGE_FEEDBACK_TYPE } from "../../../library/constants";

/**
 * EAI-1762 — thumbs up/down on an AI-generated draft, before it is sent.
 *
 * Same buttons and same drawer as a sent message (AiFeedbackButtons +
 * MessageFeedback). A draft has no message row, so the rating is keyed by the
 * draft's aiResponseHistoryId instead — messageId is nullable on
 * createMessageFeedback for this case. The drawer posts on submit; the rating
 * is then mirrored into redux (pendingDraftFeedback.text) purely as a record
 * that this draft has been voted on, so the buttons stop offering a second one.
 *
 * The buttons show only for a live, unedited, unrated AI draft — see the gate
 * below. AiFeedbackButtons additionally applies the AUTO_RESPONSE_FEEDBACK
 * setting and the AUTORESPONDER_FEEDBACK_SUBMIT permission gate.
 */
const DraftAiFeedback = ({ selectedLead, user, messageText, text, selectedOppTypeId }) => {
  const dispatch = useDispatch();
  const aiResponseHistoryId = useSelector(getTextAiResponseHistoryId);
  const pendingFeedback = useSelector(getPendingTextDraftFeedback);
  // What Otto actually wrote, captured when the draft was generated.
  const aiDraftContent = useSelector(getTextAiDraftContent);
  // Opportunity scope of the draft, from the same two sources createMessage
  // uses: the opp pinned in the toolbar dropdown (TopToolbarJsx) or, when that
  // is an "All <root>" row, the active root opp-type tab.
  const { messageOpportunity } = useSelector(state => state.lead);
  const [form] = Form.useForm();
  const [feedback, setFeedback] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // The textarea is uncontrolled but its onChange mirrors every keystroke into
  // `messageText`, so this tracks the box being edited or cleared. `text.current`
  // is the fallback for the AI typing animation's first frames.
  const draftContent = messageText || text?.current?.value || "";
  const hasDraft = !!draftContent.trim();

  // A freshly generated draft is a new thing to rate — drop the thumb state and
  // any rating still parked for the draft it replaced. Without the clear, that
  // stale rating sits in redux with the old history id: the flush at send time
  // rejects it (ids no longer match) but it would keep shadowing this draft's
  // own rating until then.
  useEffect(() => {
    setFeedback(null);
    setIsDrawerOpen(false);
    form.resetFields();
    if (
      pendingFeedback?.aiResponseHistoryId &&
      +pendingFeedback.aiResponseHistoryId !== +aiResponseHistoryId
    ) {
      dispatch(clearPendingTextDraftFeedback());
    }
  }, [aiResponseHistoryId]);

  // Clearing the box removes the very content that was rated, so discard the
  // rating too — otherwise it would attach to whatever gets typed next.
  useEffect(() => {
    if (hasDraft) return;
    setFeedback(null);
    setIsDrawerOpen(false);
    form.resetFields();
    if (pendingFeedback) {
      dispatch(clearPendingTextDraftFeedback());
    }
  }, [hasDraft]);

  // A rating already given for THIS draft. The drawer posts on submit, so this
  // is the record of a vote that is already in — not a pending one.
  const alreadyRated = !!(
    pendingFeedback?.aiResponseHistoryId &&
    +pendingFeedback.aiResponseHistoryId === +aiResponseHistoryId &&
    pendingFeedback?.rating
  );

  // Whether the box still holds exactly what Otto wrote. Once the user edits a
  // single character the text is no longer Otto's, so rating it would attribute
  // the user's own wording to the AI. Compared trimmed, since the composer
  // trims on send anyway.
  const isUneditedAiDraft = !!aiDraftContent && draftContent.trim() === aiDraftContent.trim();

  // Hidden when: no AI draft in the composer, the box is empty, this draft was
  // already rated (one vote per draft), or the user has edited Otto's text.
  if (!aiResponseHistoryId || !hasDraft || alreadyRated || !isUneditedAiDraft) return null;

  const handleFeedback = type => {
    setFeedback(feedback === type ? null : type);
    setIsDrawerOpen(true);
  };

  const handleFeedbackSubmit = values => {
    dispatch(
      setPendingTextDraftFeedback({
        aiResponseHistoryId,
        rating: values.feedback,
        feedbackType: values.feedbackType || MESSAGE_FEEDBACK_TYPE.DRAFT,
        feedbackOptionIds: values.feedbackOptionIds || [],
        additionalComments: values.feedbackNote || "",
        // Opportunity scope, same convention as the createMessage payload —
        // both fields are sent and the BE picks. The toolbar dropdown drives
        // them: a specific opp sets leadOpportunityId AND its root type id;
        // "All <root>" sets only selectedOppTypeId (resolveLeadOpportunityId
        // returns null for the #DEFAULTOPPID sentinel); "All opportunities"
        // sets neither. selectedOppTypeId is a root opp_TYPE id — not a
        // status id.
        leadOpportunityId: resolveLeadOpportunityId(messageOpportunity),
        selectedOppTypeId: selectedOppTypeId || null,
      })
    );
    setIsDrawerOpen(false);
    form.resetFields();
  };

  const handleDrawerClose = closeFlag => {
    setIsDrawerOpen(false);
    if (closeFlag) {
      setFeedback(null);
    }
    form.resetFields();
  };

  return (
    <>
      {/* The confirmation is a toast here, not inline text — there is no room
          for a sentence in the composer corner. Hence isFeedbackSent={false}. */}
      <AiFeedbackButtons
        feedback={feedback}
        messageFeedback=""
        isFeedbackSent={false}
        onFeedback={handleFeedback}
        containerClassName="flex items-center"
      />
      {isDrawerOpen && (
        <MessageFeedback
          isDraft
          feedbackType={MESSAGE_FEEDBACK_TYPE.DRAFT}
          aiResponseHistoryId={aiResponseHistoryId}
          isDrawerOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onFeedbackSubmit={handleFeedbackSubmit}
          form={form}
          messageId={null}
          user={user}
          feedback={feedback || ""}
          messageContent={draftContent}
          messageContentLabel="Draft Message"
          showReasonLink={false}
          lead={selectedLead}
        />
      )}
    </>
  );
};

export default DraftAiFeedback;
